import { createFileRoute } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import { KOMMUN_LINKS, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN, LAST_REVIEWED } from "@/lib/official-sources";

const faqs = [
  { q: "Vart ansöker jag om serveringstillstånd i Danderyd?", a: "Ansökan görs till tillståndsenheten i Danderyds kommun. Verksamhet i annan kommun (t.ex. Täby, Stockholm, Sollentuna) söker hos respektive kommun." },
  { q: "Vad kostar serveringstillstånd i Danderyd?", a: "Danderyds kommun tar ut en ansökningsavgift och en årlig tillsynsavgift beroende på tillståndstyp och alkoholomsättning – aktuella avgifter finns på kommunens webbplats." },
  { q: "Hur lång är handläggningstiden i Danderyd?", a: "Räkna med 8–12 veckor för en komplett ansökan om stadigvarande tillstånd." },
];

export const Route = createFileRoute("/serveringstillstand-danderyd")({
  component: () => (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Guider", to: "/serveringstillstand-och-kunskapsprov" },
        { label: "Danderyd" },
      ]}
      title="Serveringstillstånd Danderyd – ansökan och krav"
      intro="Allt du behöver veta om serveringstillstånd i Danderyd: var du ansöker, vilka krav som ställs och hur du förbereder dig för kunskapsprovet."
      lastReviewed={LAST_REVIEWED}
      sources={[KOMMUN_LINKS.danderyd, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN]}
      sections={[
        { h2: "Var ansöker du i Danderyd?", body: "Tillståndsenheten i Danderyds kommun ansvarar för serveringstillstånd. För verksamhet i angränsande kommuner som Täby, Sollentuna, Lidingö eller Stockholm ansöker du hos respektive kommun." },
        { h2: "Krav från Danderyds kommun", body: "Utöver de nationella kraven (personlig och ekonomisk lämplighet, godkänt kunskapsprov och lämplig lokal) har Danderyd en villa- och centrumdominerad struktur runt Mörby centrum och Djursholm. Kommunens riktlinjer om öppettider och uteservering är strikta där bostäder ligger nära serveringen. Från 1 juni 2026 är kraven på eget kök och varierat matutbud borttagna nationellt." },
        { h2: "Kunskapsprovet i Danderyd", body: "Provet genomförs hos kommunen efter att ansökan har kommit in. Skriftligt prov inom alkohollagen, serveringsbestämmelser, ordning, nykterhet och marknadsföring enligt FoHMFS 2014:7. Träna i samma format med Alkoholprovet." },
        { h2: "Handläggningstid", body: "Räkna med 8–12 veckor från komplett ansökan till beslut. Sök i god tid inför säsong eller nyöppning." },
        { h2: "Tillsyn och uppföljning", body: "Danderyds kommun bedriver tillsyn tillsammans med polis och Skatteverket. Anmärkningar kan leda till erinran, varning, sanktionsavgift eller återkallelse av tillstånd." },
        { h2: "Lokala särdrag – Mörby centrum och Djursholm", body: "Danderyd har relativt få serveringsställen jämfört med grannkommunerna, men höga krav på ordning och lokal profil, särskilt runt Mörby centrum och de villadominerade områdena Djursholm och Stocksund." },
      ]}
      faqs={faqs}
      related={{
        title: "Relaterade sidor",
        links: [
          { to: "/serveringstillstand-och-kunskapsprov", label: "Hub – alla guider" },
          { to: "/serveringstillstand-taby", label: "Serveringstillstånd Täby" },
          { to: "/serveringstillstand-lidingo", label: "Serveringstillstånd Lidingö" },
          { to: "/serveringstillstand-sollentuna", label: "Serveringstillstånd Sollentuna" },
          { to: "/serveringstillstand-restaurang", label: "För restaurang" },
          { to: "/tillfalligt-serveringstillstand", label: "Tillfälligt tillstånd" },
          { to: "/kunskapsprov-alkohollagen", label: "Kunskapsprov i alkohollagen" },
          { to: "/foretag", label: "Företagslösning" },
        ],
      }}
    />
  ),
  head: () => ({
    meta: [
      { title: "Serveringstillstånd Danderyd – krav, prov & tips" },
      { name: "description", content: "Så får du serveringstillstånd i Danderyd: lokala krav, tillsynsavgifter och hur du klarar kunskapsprovet." },
      { property: "og:title", content: "Serveringstillstånd Danderyd – tips" },
      { property: "og:description", content: "Ansökan, krav, avgifter och kunskapsprov för serveringstillstånd i Danderyd." },
      { property: "og:url", content: "https://alkoholprovet.se/serveringstillstand-danderyd" },
    ],
    links: [{ rel: "canonical", href: "https://alkoholprovet.se/serveringstillstand-danderyd" }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Guider", url: "https://alkoholprovet.se/serveringstillstand-och-kunskapsprov" },
        { label: "Danderyd", url: "https://alkoholprovet.se/serveringstillstand-danderyd" },
      ]),
    ],
  }),
});
